import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Layout } from "../components/Navbar";
import { api, getUser } from "../api";
import { toast } from "sonner";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const emptyForm = () => ({
  name: "", description: "", price_cents: "", duration_minutes: 30, location_label: "", address: "",
  capacity: 1, professional_mode: "none", professional_id: null, professional_free_name: "", reminder_minutes: 0,
  weekly: DAYS.map((_, i) => ({ weekday: i, enabled: false, start: "09:00", end: "13:00" })), active: true,
});

export default function OrgServices() {
  const { t } = useTranslation();
  const u = getUser();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // form object or null
  const load = () => api.get("/org/services").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  if (!u || u.role !== "organization") return <Layout><div className="yp-container py-8" data-testid="services-forbidden">{t("events.only_org")}</div></Layout>;

  const startCreate = () => setEditing(emptyForm());
  const startEdit = (s) => setEditing({
    ...emptyForm(), ...s, price_cents: s.price_cents ? s.price_cents / 100 : "",
    weekly: DAYS.map((_, i) => {
      const r = (s.weekly || []).find((w) => w.weekday === i);
      return { weekday: i, enabled: !!r, start: r?.start || "09:00", end: r?.end || "13:00" };
    }),
  });

  const invite = async () => {
    const { data } = await api.post("/org/invite-professional");
    try { await navigator.clipboard.writeText(data.invite_url); } catch {}
    toast.success(t("services.invite_done"));
  };

  return (
    <Layout><div className="yp-container max-w-3xl py-8" data-testid="org-services-page">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-2">
        <h1 className="text-3xl font-bold">{t("services.manage_title")}</h1>
        {!editing && <button className="yp-btn yp-btn-primary" onClick={startCreate} data-testid="service-create-btn">+ {t("services.create")}</button>}
      </div>

      {editing ? (
        <ServiceForm form={editing} setForm={setEditing} onDone={() => { setEditing(null); load(); }} onInvite={invite} t={t} />
      ) : items === null ? <div className="text-slate-400">{t("common.loading")}</div>
        : items.length === 0 ? <div className="text-slate-400" data-testid="services-empty">{t("services.empty")}</div>
          : (<div className="space-y-3" data-testid="services-list">
            {items.map((s) => (
              <div key={s.id} className={`yp-card p-4 ${s.active ? "" : "opacity-60"}`} data-testid="service-row">
                <div className="flex justify-between items-start gap-3 flex-wrap">
                  <div>
                    <div className="font-semibold" data-testid="service-row-name">{s.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{s.price_cents ? `€ ${(s.price_cents / 100).toFixed(2)}` : t("services.free")} · {s.duration_minutes} min · {s.location_label || "—"}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{s.professional ? (s.professional.free ? `${t("services.mode_free")}: ${s.professional.name}` : `@${s.professional.name}`) : t("services.no_professional")}</div>
                    <span className={`yp-badge ${s.active ? "yp-badge-approved" : "yp-badge-rejected"} mt-2`}>{s.active ? t("services.active") : t("services.inactive")}</span>
                  </div>
                  <div className="flex gap-2">
                    <a href={`${window.location.origin}/services/${s.id}`} target="_blank" rel="noopener noreferrer" className="yp-btn yp-btn-outline !py-1 !px-3 text-xs" data-testid="service-preview-btn" title={t("events.preview_hint")}>{t("events.preview")}</a>
                    <button className="yp-btn yp-btn-outline !py-1 !px-3 text-xs" onClick={() => startEdit(s)} data-testid="service-edit-btn">{t("services.edit")}</button>
                    {s.active && <button className="yp-btn yp-btn-ghost !py-1 !px-3 text-xs" onClick={async () => { await api.delete(`/org/services/${s.id}`); toast.success(t("services.deleted")); load(); }} data-testid="service-delete-btn">{t("services.delete")}</button>}
                  </div>
                </div>
              </div>))}
          </div>)}
    </div></Layout>
  );
}

function ServiceForm({ form, setForm, onDone, onInvite, t }) {
  const [proQuery, setProQuery] = useState("");
  const [proResults, setProResults] = useState([]);
  const timer = useRef(null);
  const searchPro = (q) => {
    setProQuery(q);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { if (q.trim()) api.get("/professionals-search", { params: { q } }).then(({ data }) => setProResults(data)).catch(() => setProResults([])); else setProResults([]); }, 250);
  };
  const save = async () => {
    const weekly = form.weekly.filter((w) => w.enabled).map((w) => ({ weekday: w.weekday, start: w.start, end: w.end }));
    const body = {
      name: form.name, description: form.description, price_cents: form.price_cents ? Math.round(Number(form.price_cents) * 100) : 0,
      duration_minutes: Number(form.duration_minutes), location_label: form.location_label, address: form.address || form.location_label,
      capacity: Number(form.capacity) || 1, professional_mode: form.professional_mode,
      professional_id: form.professional_mode === "pro" ? form.professional_id : null,
      professional_free_name: form.professional_mode === "free" ? form.professional_free_name : null,
      reminder_minutes: Number(form.reminder_minutes) || 0, weekly, active: form.active,
    };
    if (!body.name.trim()) { toast.error(t("services.err_name")); return; }
    if (form.professional_mode === "pro" && !body.professional_id) { toast.error(t("services.err_pro")); return; }
    try {
      if (form.id) await api.put(`/org/services/${form.id}`, body);
      else await api.post("/org/services", body);
      toast.success(t("services.saved")); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || t("events.error_generic")); }
  };
  const upd = (k, v) => setForm({ ...form, [k]: v });
  const updDay = (i, k, v) => setForm({ ...form, weekly: form.weekly.map((w, j) => j === i ? { ...w, [k]: v } : w) });
  return (
    <div className="yp-card p-6 space-y-4" data-testid="service-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className="text-sm font-medium">{t("services.name")}</label><input className="yp-input" value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="service-name-input" /></div>
        <div><label className="text-sm font-medium">{t("services.price")}</label><input type="number" step="0.01" min="0" className="yp-input" value={form.price_cents} onChange={(e) => upd("price_cents", e.target.value)} data-testid="service-price-input" /></div>
      </div>
      <div><label className="text-sm font-medium">{t("services.description")}</label><textarea className="yp-input" rows={2} value={form.description} onChange={(e) => upd("description", e.target.value)} data-testid="service-desc-input" /></div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div><label className="text-sm font-medium">{t("services.duration")}</label>
          <select className="yp-input" value={form.duration_minutes} onChange={(e) => upd("duration_minutes", e.target.value)} data-testid="service-duration-input">
            {[15, 20, 30, 45, 60].map((d) => <option key={d} value={d}>{d} min</option>)}
          </select></div>
        <div><label className="text-sm font-medium">{t("services.capacity")}</label><input type="number" min="1" className="yp-input" value={form.capacity} onChange={(e) => upd("capacity", e.target.value)} data-testid="service-capacity-input" /></div>
        <div><label className="text-sm font-medium">{t("services.reminder")}</label>
          <select className="yp-input" value={form.reminder_minutes} onChange={(e) => upd("reminder_minutes", e.target.value)} data-testid="service-reminder-input">
            <option value={0}>{t("services.reminder_off")}</option>
            {[60, 120, 180, 1440].map((m) => <option key={m} value={m}>{m >= 1440 ? "1 " + t("services.day") : m / 60 + "h"}</option>)}
          </select></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className="text-sm font-medium">{t("services.location")}</label><input className="yp-input" value={form.location_label} onChange={(e) => upd("location_label", e.target.value)} data-testid="service-location-input" /></div>
        <div><label className="text-sm font-medium">{t("services.address")}</label><input className="yp-input" value={form.address} onChange={(e) => upd("address", e.target.value)} data-testid="service-address-input" /></div>
      </div>

      <div>
        <label className="text-sm font-medium">{t("services.professional_mode")}</label>
        <select className="yp-input" value={form.professional_mode} onChange={(e) => upd("professional_mode", e.target.value)} data-testid="service-promode-select">
          <option value="none">{t("services.mode_none")}</option>
          <option value="free">{t("services.mode_free")}</option>
          <option value="pro">{t("services.mode_pro")}</option>
        </select>
        {form.professional_mode === "free" && (
          <input className="yp-input mt-2" placeholder={t("services.free_name")} value={form.professional_free_name || ""} onChange={(e) => upd("professional_free_name", e.target.value)} data-testid="service-freename-input" />)}
        {form.professional_mode === "pro" && (
          <div className="mt-2">
            {form.professional_id ? (
              <div className="flex items-center gap-2 text-sm"><span className="yp-badge yp-badge-approved" data-testid="service-selected-pro">@{form.professional_name || form.professional_id}</span>
                <button className="yp-btn yp-btn-ghost !py-1 !px-2 text-xs" onClick={() => setForm({ ...form, professional_id: null, professional_name: "" })}>×</button></div>
            ) : (<>
              <input className="yp-input" placeholder={t("services.pick_pro")} value={proQuery} onChange={(e) => searchPro(e.target.value)} data-testid="service-pro-search" />
              {proResults.length > 0 && (
                <div className="mt-1 border border-slate-200 rounded max-h-40 overflow-y-auto" data-testid="service-pro-results">
                  {proResults.map((p) => (
                    <button key={p.id} className="block w-full text-left px-3 py-2 hover:bg-slate-50 text-sm" onClick={() => { setForm({ ...form, professional_id: p.id, professional_name: p.name }); setProResults([]); setProQuery(""); }} data-testid="service-pro-option">{p.name}{p.specialization ? ` · ${p.specialization}` : ""}</button>))}
                </div>)}
              <button className="yp-btn yp-btn-outline !py-1 !px-3 text-xs mt-2" onClick={onInvite} data-testid="service-invite-pro-btn">{t("services.invite_pro")}</button>
            </>)}
          </div>)}
      </div>

      <div>
        <label className="text-sm font-medium">{t("services.schedule")}</label>
        <div className="space-y-1 mt-1">
          {form.weekly.map((w, i) => (
            <div key={i} className="flex items-center gap-2 text-sm" data-testid="service-day-row">
              <label className="flex items-center gap-1 w-20"><input type="checkbox" checked={w.enabled} onChange={(e) => updDay(i, "enabled", e.target.checked)} data-testid={`service-day-${DAYS[i]}`} />{t(`services.d_${DAYS[i]}`)}</label>
              <input type="time" className="yp-input !py-1 !w-auto" value={w.start} disabled={!w.enabled} onChange={(e) => updDay(i, "start", e.target.value)} />
              <span>–</span>
              <input type="time" className="yp-input !py-1 !w-auto" value={w.end} disabled={!w.enabled} onChange={(e) => updDay(i, "end", e.target.value)} />
            </div>))}
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => upd("active", e.target.checked)} data-testid="service-active-toggle" />{t("services.active")}</label>

      <div className="flex gap-2">
        <button className="yp-btn yp-btn-primary" onClick={save} data-testid="service-save-btn">{t("services.save")}</button>
        <button className="yp-btn yp-btn-ghost" onClick={onDone} data-testid="service-cancel-btn">{t("services.cancel")}</button>
      </div>
    </div>);
}
